import Editor from "@monaco-editor/react";
import { debounce } from "lodash";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import Timer from "./Timer";
import Toolbar from "../../components/CodingToolbar";
import { useNavigate, useParams } from "react-router-dom";
import host from "../../global";
import { candidateAuthHeader } from "../../utils/candidateAuth";
import { toast, ToastContainer } from "react-toastify";
import { IoWarningOutline } from "react-icons/io5";
import { useTheme } from "../../hooks/useTheme";
import CodingProctoringSystem from "./CodingProctoringSystem";
import AlertPanel from "./Interviews/components/AlertPanel";
import ProctoringAlertModal from "./Interviews/components/ProctoringAlertModal";
import { COOLDOWN_PERIOD } from "./Interviews/constants";
import ApexDescription from "../../components/common/ApexDescription";

// ─── Language config ──────────────────────────────────────────────────────────
// No hardcoded language list here — the dropdown is built entirely from the
// admin's allowed_languages for this test (fetched from the database). This map
// only translates those Title-Case names (e.g. "C++") into the lowercase codes
// the editor/Monaco/boilerplate API need (e.g. "cpp") — it isn't a list of options.
const LANG_NAME_TO_VALUE = {
  Python: "python",
  JavaScript: "javascript",
  Java: "java",
  "C++": "cpp",
  "C#": "cs",
  Go: "go",
  Rust: "rust",
  Apex: "apex", // Monaco ships an "apex" grammar; Apex answers are AI-reviewed, never executed
};

// Monaco's built-in language id for C# is "csharp", not our internal "cs" code.
const toMonacoLanguage = (lang) => (lang === "cs" ? "csharp" : lang);

const FONT_SIZES = [12, 13, 14, 15, 16, 18, 20];

// ─── Difficulty badge ─────────────────────────────────────────────────────────
const DifficultyBadge = ({ level }) => {
  const cfg = {
    Easy:   { bg: "bg-green-100",  text: "text-green-700",  ring: "ring-green-300" },
    Medium: { bg: "bg-amber-100",  text: "text-amber-700",  ring: "ring-amber-300" },
    Hard:   { bg: "bg-red-100",    text: "text-red-600",    ring: "ring-red-300" },
  }[level] || { bg: "bg-gray-100", text: "text-gray-600", ring: "ring-gray-300" };

  return (
    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ring-1 ${cfg.bg} ${cfg.text} ${cfg.ring}`}>
      {level}
    </span>
  );
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
const Spinner = ({ size = 16 }) => (
  <svg className="animate-spin" width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
  </svg>
);

// ─── Main component ───────────────────────────────────────────────────────────
export default function CodingPlatform() {
  const { interview_id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Core data
  const [problems, setProblems] = useState([]);
  const [currentProblem, setCurrentProblem] = useState(null);
  const [code, setCode] = useState("");
  const [language, setLanguage] = useState("python");
  const [submittedProblems, setSubmittedProblems] = useState(new Set());
  const [allowedLanguageNames, setAllowedLanguageNames] = useState([]); // Title-Case names from the admin's coding set, e.g. ["Python", "Java"]
  const [allowedLanguagesLoaded, setAllowedLanguagesLoaded] = useState(false); // becomes true once we actually know — avoids flashing the full hardcoded list before the fetch resolves

  // Timer / test status
  const [codingTimeLimit, setCodingTimeLimit] = useState(90000);
  const [coding_startTime, setCodingStartTime] = useState("");
  const [stopTest, setStopTest] = useState(false);

  // Editor options
  const [fontSize, setFontSize] = useState(14);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);

  // Description panel
  const [descTab, setDescTab] = useState("description"); // description | examples | hints

  // Bottom panel
  const [bottomTab, setBottomTab] = useState("input"); // input | testcases | output
  const [bottomPanelHeight, setBottomPanelHeight] = useState(200);
  const [customInput, setCustomInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [testResults, setTestResults] = useState([]);
  const [activeTestCase, setActiveTestCase] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hiddenTestCounts, setHiddenTestCounts] = useState(null); // { passed, failed, total } | null

  // Modal
  const [showDialog, setShowDialog] = useState(false);
  const [isEndingTest, setIsEndingTest] = useState(false);

  // Proctoring
  const [isVideoProctoringEnabled, setIsVideoProctoringEnabled] = useState(false);
  const [alertLog, setAlertLog] = useState({
    tab_switch: { count: 0, lastAlertTime: null, glowing: false },
    multi_monitor: { count: 0, lastAlertTime: null, glowing: false },
    fullscreen_exit: { count: 0, lastAlertTime: null, glowing: false },
  });
  const [stickyAlertCount, setStickyAlertCount] = useState(0);
  const [isAlertPanelOpen, setIsAlertPanelOpen] = useState(false);
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);
  const [showProctoringAlert, setShowProctoringAlert] = useState(false);
  const [proctoringAlertMessage, setProctoringAlertMessage] = useState("");

  // Drag refs
  const separatorRef = useRef(null);
  const descriptionRef = useRef(null);
  const codeRef = useRef(null);
  const bottomResizeRef = useRef(null);
  const langDropdownRef = useRef(null);
  const fontDropdownRef = useRef(null);

  // ── Auto-close alert panel (unless a fullscreen alert is active) ───────────
  useEffect(() => {
    const isFullscreenAlertActive = alertLog.fullscreen_exit.count > 0;
    if (isAlertPanelOpen && !isFullscreenAlertActive) {
      const timer = setTimeout(() => setIsAlertPanelOpen(false), COOLDOWN_PERIOD.ALERT_PANEL_AUTO_CLOSE_MS);
      return () => clearTimeout(timer);
    }
  }, [isAlertPanelOpen, alertLog.fullscreen_exit.count]);

  // ── Close dropdowns on outside click ────────────────────────────────────────
  useEffect(() => {
    const close = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) setLangDropdownOpen(false);
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(e.target)) setFontDropdownOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // ── Fetch time limit ─────────────────────────────────────────────────────────
  useEffect(() => {
    const getCandidateStatus = async () => {
      try {
        const startDateTime = new Date().toUTCString();
        const response = await axios.get(`${host}/get_coding_timelimit`, {
          params: { interview_id, startDateTime },
          headers: candidateAuthHeader(interview_id),
        });
        setCodingStartTime(new Date(response.data.coding_start_time));
        setCodingTimeLimit(response.data.remaining_seconds);
        if (response.data.coding_status === "Complete") setStopTest(true);
      } catch {
        toast.error("Failed to fetch candidate status", { hideProgressBar: true });
      }
    };
    getCandidateStatus();
  }, [interview_id]);

  // ── Fetch proctoring configuration ──────────────────────────────────────────
  useEffect(() => {
    const fetchConfiguration = async () => {
      try {
        const response = await fetch(`${host}/get_configurations_wo_auth/${interview_id}`);
        const data = await response.json();
        setIsVideoProctoringEnabled(data.video_included || false);
      } catch {
        setIsVideoProctoringEnabled(false);
      }
    };
    fetchConfiguration();
  }, [interview_id]);

  // ── Fetch problems ───────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchProblems = async () => {
      try {
        const response = await axios.get(`${host}/api/coding_problems/${interview_id}`, {
          headers: candidateAuthHeader(interview_id),
        });
        setProblems(response.data);
        setAllowedLanguageNames(response.data[0]?.allowed_languages || []);
        if (response.data.length > 0) {
          setCurrentProblem(response.data[0]);
          await loadCode(response.data[0], language);
        }
      } catch {
        toast.error("Failed to load problems", { hideProgressBar: true });
      } finally {
        setAllowedLanguagesLoaded(true);
      }
    };
    fetchProblems();
  }, [interview_id]);

  // Drives the dropdown entirely from allowed_languages (the admin's configured
  // set) — there is no hardcoded fallback list. Nothing renders until the fetch
  // resolves; if the admin configured nothing, the list is genuinely empty.
  const availableLanguages = useMemo(() => {
    if (!allowedLanguagesLoaded) return [];
    return allowedLanguageNames
      .map((name) => {
        const value = LANG_NAME_TO_VALUE[name];
        return value ? { value, label: name } : null;
      })
      .filter(Boolean);
  }, [allowedLanguagesLoaded, allowedLanguageNames]);

  // If the currently selected language isn't in the allowed set (e.g. the
  // default "python" wasn't picked by the admin), switch to the first allowed one.
  useEffect(() => {
    if (availableLanguages.length === 0) return;
    if (!availableLanguages.some((l) => l.value === language)) {
      handleLanguageChange(availableLanguages[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableLanguages]);

  // ── Auto-save to localStorage ────────────────────────────────────────────────
  useEffect(() => {
    const save = debounce(() => {
      if (currentProblem && code) {
        localStorage.setItem(`${language}_${currentProblem.id}`, code);
      }
    }, 2000);
    save();
    return () => save.cancel();
  }, [code, currentProblem, language]);

  // ── Load code on language/problem change ─────────────────────────────────────
  useEffect(() => {
    if (currentProblem) loadCode(currentProblem, language);
  }, [language, currentProblem]);

  const loadBoilerplateCode = async (problemId, lang) => {
    try {
      const response = await axios.get(`${host}/get_problem_code/${problemId}/${lang}`);
      return response.data.code || "";
    } catch {
      return "";
    }
  };

  const loadCode = async (problem, lang) => {
    if (!problem) return;
    const stored = localStorage.getItem(`${lang}_${problem.id}`);
    if (stored) { setCode(stored); return; }
    const boilerplate = await loadBoilerplateCode(problem.id, lang);
    setCode(boilerplate);
    if (boilerplate) localStorage.setItem(`${lang}_${problem.id}`, boilerplate);
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    setLangDropdownOpen(false);
    if (currentProblem) await loadCode(currentProblem, lang);
  };

  const handleProblemClick = async (id) => {
    const problem = problems.find((p) => p.id === id);
    if (problem) {
      setCurrentProblem(problem);
      await loadCode(problem, language);
      setTestResults([]);
      setHiddenTestCounts(null);
      setOutput("");
      setError("");
      setDescTab("description");
    }
  };

  const handleResetCode = async () => {
    if (!currentProblem) return;
    localStorage.removeItem(`${language}_${currentProblem.id}`);
    const boilerplate = await loadBoilerplateCode(currentProblem.id, language);
    setCode(boilerplate);
    toast.info("Code reset to boilerplate", { hideProgressBar: true });
  };

  const handleRun = async () => {
    if (!currentProblem) return;
    setIsRunning(true);
    setOutput("");
    setError("");
    setBottomTab("output");
    try {
      const { data } = await axios.post(`${host}/api/problems/${currentProblem.id}/submit`, {
        code, language, input: customInput,
      }, { params: { interview_id }, headers: candidateAuthHeader(interview_id) });
      setError(data.compile_output || data.stderr || "");
      setOutput(data.stdout || "");
    } catch {
      setError("Error: Failed to execute code");
    } finally {
      setIsRunning(false);
    }
  };

  const handleRunTestCases = async () => {
    if (!currentProblem) return;
    setIsRunning(true);
    setBottomTab("testcases");
    try {
      const { data } = await axios.post(
        `${host}/api/problems/${currentProblem.id}/run_test_case`,
        { code, language, input: "" },
        { params: { interview_id }, headers: candidateAuthHeader(interview_id) }
      );
      const visibleResults = data.visible ?? data; // backward compat if shape changes
      const testCases = currentProblem.test_cases || [];
      setTestResults(
        visibleResults.map((r, i) => ({
          index: i + 1,
          input: testCases[i]?.input || "",
          desiredOutput: r.expectedOutput ?? testCases[i]?.output ?? "",
          yourOutput: r.yourOutput,
          status: r.status_message,
        }))
      );
      setHiddenTestCounts(
        data.hidden_total > 0
          ? { passed: data.hidden_passed, failed: data.hidden_failed, total: data.hidden_total }
          : null
      );
      setActiveTestCase(0);
    } catch {
      toast.error("Failed to submit code", { hideProgressBar: true });
    } finally {
      setIsRunning(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (!currentProblem) return;
    setIsSubmitting(true);
    try {
      const { data } = await axios.post(`${host}/api/submit_code/${currentProblem.id}`, {
        code, language, interview_id,
      }, { headers: candidateAuthHeader(interview_id) });
      setSubmittedProblems((prev) => new Set([...prev, currentProblem.id]));
      const resultSuffix = typeof data?.test_cases_passed_count === "number"
        ? ` ${data.test_cases_passed_count}/${data.test_cases_total} test cases passed.`
        : "";
      const message = data?.evaluation_status
        ? "Answer submitted. It will be reviewed after the test."
        : `Code submitted!${resultSuffix}`;
      toast.success(`${message} Proceed to next question or End Test.`, { hideProgressBar: true });
    } catch {
      toast.error("Failed to submit code", { hideProgressBar: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h > 0 ? String(h).padStart(2, "0") + ":" : ""}${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const EndTest = async () => {
    setShowDialog(false);
    setIsEndingTest(true);
    const elapsed = Math.floor(Date.now() / 1000) - Math.floor(new Date(coding_startTime).getTime() / 1000);
    const timeTaken = formatTime(elapsed);
    try {
      for (const problem of problems) {
        const savedCode = localStorage.getItem(`${language}_${problem.id}`);
        if (savedCode) {
          try {
            await axios.post(`${host}/api/submit_code/${problem.id}`, {
              code: savedCode, language, interview_id,
            }, { headers: candidateAuthHeader(interview_id) });
          } catch {}
        }
      }
      const candidateToken = sessionStorage.getItem(`candidateToken_${interview_id}`);
      await fetch(`${host}/save_coding_test/${interview_id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(candidateToken ? { Authorization: `Bearer ${candidateToken}` } : {}),
        },
        body: JSON.stringify({ problem_ids: problems.map((p) => p.id), time_taken: timeTaken }),
      });
      localStorage.clear();
      toast.success("Test submitted successfully!", { hideProgressBar: true });
      setTimeout(() => navigate(`/candidate/${interview_id}/testhomepage`), 3000);
    } catch {
      toast.error("Error submitting test results", { hideProgressBar: true });
    }
  };

  // ── Proctoring handlers ──────────────────────────────────────────────────────
  const enterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) elem.requestFullscreen();
    else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
    else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
    else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
  };

  const getActiveAlerts = () => {
    const alerts = [];
    if (alertLog.fullscreen_exit.count > 0) {
      return [{
        type: "fullscreen_exit",
        message: "Please enter fullscreen mode to continue",
        count: alertLog.fullscreen_exit.count,
        lastAlertTime: alertLog.fullscreen_exit.lastAlertTime,
        glowing: alertLog.fullscreen_exit.glowing,
        isClickable: true,
      }];
    }
    if (alertLog.tab_switch.count > 0) {
      alerts.push({
        type: "tab_switch",
        message: "Tab switch detected",
        count: alertLog.tab_switch.count,
        lastAlertTime: alertLog.tab_switch.lastAlertTime,
        glowing: alertLog.tab_switch.glowing,
        isClickable: false,
      });
    }
    if (alertLog.multi_monitor.count > 0) {
      alerts.push({
        type: "multi_monitor",
        message: "Multiple monitors detected",
        count: alertLog.multi_monitor.count,
        lastAlertTime: alertLog.multi_monitor.lastAlertTime,
        glowing: alertLog.multi_monitor.glowing,
        isClickable: false,
      });
    }
    return alerts.sort((a, b) => (b.lastAlertTime || 0) - (a.lastAlertTime || 0));
  };

  const handleAlertIconClick = () => {
    setIsAlertPanelOpen((open) => !open);
    if (!isAlertPanelOpen) {
      setStickyAlertCount(0);
      setCurrentAlertIndex(0);
      setAlertLog((prev) => ({
        ...prev,
        tab_switch: { ...prev.tab_switch, glowing: false },
        multi_monitor: { ...prev.multi_monitor, glowing: false },
        fullscreen_exit: { ...prev.fullscreen_exit, glowing: false },
      }));
    }
  };

  const navigateToNextAlert = () => {
    const activeAlerts = getActiveAlerts();
    setCurrentAlertIndex((prev) => (prev + 1) % activeAlerts.length);
  };

  const navigateToPrevAlert = () => {
    const activeAlerts = getActiveAlerts();
    setCurrentAlertIndex((prev) => (prev - 1 + activeAlerts.length) % activeAlerts.length);
  };

  const handleAlertClick = (alertType) => {
    if (alertType === "fullscreen_exit") enterFullscreen();
  };

  const handleStickyAlert = (alertType) => {
    setStickyAlertCount((prev) => prev + 1);
    if (alertType === "fullscreen_exit") setIsAlertPanelOpen(true);
    setAlertLog((prev) => {
      const now = Date.now();
      const prevAlert = prev[alertType];
      const newCount = (prevAlert?.count || 0) + 1;
      const shouldGlow = !prevAlert?.lastAlertTime || now - prevAlert.lastAlertTime >= COOLDOWN_PERIOD.ALERT_WINDOW_MS;
      return { ...prev, [alertType]: { count: newCount, lastAlertTime: now, glowing: shouldGlow } };
    });
  };

  const handleFullscreenExitViolation = () => {
    setProctoringAlertMessage(
      "We detected you exited fullscreen mode. Please remain in fullscreen throughout the coding challenge to ensure proper monitoring."
    );
    setShowProctoringAlert(true);
  };

  const handleTabSwitchViolation = () => {
    setProctoringAlertMessage(
      "We detected tab switching activity. Please stay on the coding challenge tab throughout the session."
    );
    setShowProctoringAlert(true);
  };

  const handleProctoringAcknowledge = () => {
    setShowProctoringAlert(false);
    enterFullscreen();
  };

  // ── Horizontal drag (description ↔ code) ────────────────────────────────────
  const handleMouseDown = (e) => {
    e.preventDefault();
    const onMove = (ev) => {
      if (!separatorRef.current || !descriptionRef.current || !codeRef.current) return;
      const containerWidth = separatorRef.current.parentElement.offsetWidth;
      const pct = (ev.clientX / containerWidth) * 100;
      const clamped = Math.max(25, Math.min(75, pct));
      descriptionRef.current.style.width = `${clamped}%`;
      codeRef.current.style.width = `${100 - clamped}%`;
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // ── Vertical drag (editor ↔ bottom panel) ───────────────────────────────────
  const handleBottomMouseDown = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = bottomPanelHeight;
    const onMove = (ev) => {
      const delta = startY - ev.clientY;
      setBottomPanelHeight(Math.max(120, Math.min(500, startH + delta)));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // ── Already-completed guard ──────────────────────────────────────────────────
  if (stopTest) {
    return (
      <div className="flex items-center justify-center h-full bg-gradient-to-br from-pink-50 to-blue-50">
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-blue-500 flex items-center justify-center mx-auto mb-4">
            <IoWarningOutline className="text-white text-3xl" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Test Already Completed</h2>
          <p className="text-gray-500">Only one attempt is allowed. You may close this window.</p>
        </div>
      </div>
    );
  }

  const currentLang =
    availableLanguages.find((l) => l.value === language) ||
    availableLanguages[0] ||
    (language ? { value: language, label: language.charAt(0).toUpperCase() + language.slice(1) } : { value: "", label: "" });
  const solvedCount = submittedProblems.size;
  // Apex questions are reviewed by AI against a rubric: there is nothing to run, so the
  // run / custom-input / test-case UI is hidden for them.
  const isApexProblem = currentProblem?.track === "apex";
  const isCodingTestActive = !stopTest && !!currentProblem;
  const allSubmitted = problems.length > 0 && solvedCount === problems.length;

  const handleEndTestClick = () => {
    if (allSubmitted) {
      EndTest();
    } else {
      setShowDialog(true);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className={`flex flex-col h-full overflow-hidden font-[Poppins] ${isDark ? "bg-gray-950 text-white" : "bg-gray-50 text-gray-900"}`}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── HEADER ───────────────────────────────────────────────────────────── */}
      <header className={`flex-none flex items-center justify-between px-4 h-12 border-b z-20 ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"} shadow-sm`}>
        {/* Left: brand */}
        <div className="flex items-center gap-3">
          <span className={`text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`}>Coding Assessment</span>
        </div>

        {/* Center: progress */}
        {problems.length > 0 && (
          <div className="flex items-center gap-3 flex-1 max-w-xs mx-6">
            <span className={`text-xs font-medium whitespace-nowrap ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {solvedCount} / {problems.length} solved
            </span>
            <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDark ? "bg-gray-700" : "bg-gray-200"}`}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${problems.length ? (solvedCount / problems.length) * 100 : 0}%`,
                  background: "linear-gradient(to right, #ec4899, #3b82f6)",
                }}
              />
            </div>
          </div>
        )}

        {/* Right: timer + end test */}
        <div className="flex items-center gap-3">
          <Timer timeLimit={codingTimeLimit} onTimeUp={EndTest} showUI inline />
          <button
            onClick={handleEndTestClick}
            className="px-3 py-1.5 text-xs font-semibold text-white rounded-full transition-all hover:opacity-90 active:scale-95"
            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
          >
            End Test
          </button>
        </div>
      </header>

      {/* ── BODY ─────────────────────────────────────────────────────────────── */}
      {currentProblem ? (
        <div className="flex flex-1 overflow-hidden">

          {/* ── LEFT SIDEBAR ───────────────────────────────────────────────── */}
          <aside className={`flex-none w-14 border-r flex flex-col items-center py-2 ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
            <Toolbar
              problems={problems}
              interview_id={interview_id}
              onProblemClick={handleProblemClick}
              currentProblemId={currentProblem.id}
              language={language}
              submittedProblems={submittedProblems}
            />
          </aside>

          {/* ── DESCRIPTION PANEL ──────────────────────────────────────────── */}
          <div
            ref={descriptionRef}
            className={`flex flex-col overflow-hidden border-r ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}
            style={{ width: "42%" }}
          >
            {/* Problem header */}
            <div className={`flex-none px-4 pt-4 pb-3 border-b ${isDark ? "border-gray-700" : "border-gray-100"}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <h1 className="text-base font-bold leading-tight">{currentProblem.title || currentProblem.description?.slice(0, 60)}</h1>
                <DifficultyBadge level={currentProblem.difficulty} />
              </div>
              {(isApexProblem ? currentProblem.topics : currentProblem.tags)?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(isApexProblem ? currentProblem.topics : currentProblem.tags).map((tag, i) => (
                    <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"}`}>{tag}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Tab bar */}
            <div className={`flex-none flex border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              {(isApexProblem ? ["description"] : ["description", "examples"]).map((tab) => {
                const active = descTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setDescTab(tab)}
                    className={`relative px-4 py-2.5 text-xs font-semibold capitalize transition-colors ${
                      active
                        ? isDark ? "text-white" : "text-gray-900"
                        : isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {tab}
                    {active && (
                      <span
                        className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                        style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto p-4 text-sm leading-relaxed">
              {descTab === "description" && (
                <div className="space-y-4">
                  {isApexProblem ? (
                    <ApexDescription
                      text={currentProblem.description}
                      textClassName={isDark ? "text-gray-300" : "text-gray-700"}
                      codeClassName={isDark ? "bg-gray-800 text-green-300" : "bg-gray-900 text-green-300"}
                    />
                  ) : (
                    <p className={isDark ? "text-gray-300" : "text-gray-700"}>{currentProblem.description}</p>
                  )}
                  {currentProblem.input_format && (
                    <div className={`rounded-xl p-3 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                      <h3 className={`text-xs font-bold uppercase tracking-wider mb-1.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>Input Format</h3>
                      <p className={isDark ? "text-gray-300" : "text-gray-700"}>{currentProblem.input_format}</p>
                    </div>
                  )}
                  {currentProblem.output_format && (
                    <div className={`rounded-xl p-3 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                      <h3 className={`text-xs font-bold uppercase tracking-wider mb-1.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>Output Format</h3>
                      <p className={isDark ? "text-gray-300" : "text-gray-700"}>{currentProblem.output_format}</p>
                    </div>
                  )}
                  {currentProblem.constraints && (
                    <div className={`rounded-xl p-3 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                      <h3 className={`text-xs font-bold uppercase tracking-wider mb-1.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>Constraints</h3>
                      <pre className={`text-xs font-mono whitespace-pre-wrap ${isDark ? "text-gray-300" : "text-gray-700"}`}>{currentProblem.constraints}</pre>
                    </div>
                  )}
                </div>
              )}

              {descTab === "examples" && (
                <div className="space-y-4">
                  {currentProblem.examples?.length > 0 ? currentProblem.examples.map((ex, i) => (
                    <div key={i} className={`rounded-xl overflow-hidden border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                      <div className={`px-3 py-1.5 text-xs font-bold ${isDark ? "bg-gray-800 text-gray-300 border-b border-gray-700" : "bg-gray-50 text-gray-600 border-b border-gray-200"}`}>
                        Example {i + 1}
                      </div>
                      <div className="p-3 space-y-2">
                        <div>
                          <span className={`text-xs font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>Input:</span>
                          <pre className={`mt-1 text-xs rounded-lg p-2 font-mono ${isDark ? "bg-gray-900 text-green-400" : "bg-gray-900 text-green-400"}`}>{ex.input}</pre>
                        </div>
                        <div>
                          <span className={`text-xs font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>Output:</span>
                          <pre className={`mt-1 text-xs rounded-lg p-2 font-mono ${isDark ? "bg-gray-900 text-blue-400" : "bg-gray-900 text-blue-400"}`}>{ex.output}</pre>
                        </div>
                        {ex.explanation && (
                          <div>
                            <span className={`text-xs font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>Explanation:</span>
                            <p className={`mt-1 text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>{ex.explanation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )) : (
                    <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>No examples available.</p>
                  )}
                </div>
              )}

            </div>
          </div>

          {/* ── DRAG DIVIDER ───────────────────────────────────────────────── */}
          <div
            ref={separatorRef}
            onMouseDown={handleMouseDown}
            className={`flex-none w-1.5 flex items-center justify-center cursor-col-resize group transition-colors ${isDark ? "bg-gray-800 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
          >
            <div className={`h-8 w-0.5 rounded-full transition-colors ${isDark ? "bg-gray-600 group-hover:bg-gray-400" : "bg-gray-400 group-hover:bg-gray-500"}`} />
          </div>

          {/* ── CODE PANEL ─────────────────────────────────────────────────── */}
          <div
            ref={codeRef}
            className="w-1/2 flex flex-col"
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* Editor toolbar */}
            <div className={`flex-none flex items-center gap-2 px-3 h-11 border-b ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
              {/* Language dropdown */}
              <div ref={langDropdownRef} className="relative">
                <button
                  onClick={() => setLangDropdownOpen((o) => !o)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${isDark ? "bg-gray-800 hover:bg-gray-700 text-gray-200" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
                >
                  <span>{currentLang.label}</span>
                  <svg className={`w-3 h-3 transition-transform ${langDropdownOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                </button>
                {langDropdownOpen && (
                  <div className={`absolute top-full left-0 mt-1 w-40 rounded-xl shadow-2xl border z-50 overflow-hidden ${isDark ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"}`}>
                    {!allowedLanguagesLoaded ? (
                      <div className={`px-3 py-2 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>Loading…</div>
                    ) : availableLanguages.length === 0 ? (
                      <div className={`px-3 py-2 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>No languages configured</div>
                    ) : availableLanguages.map((l) => (
                      <button
                        key={l.value}
                        onClick={() => handleLanguageChange(l.value)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                          language === l.value
                            ? "bg-gradient-to-r from-pink-500 to-blue-500 text-white"
                            : isDark ? "text-gray-300 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span className="font-medium">{l.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Font size */}
              <div ref={fontDropdownRef} className="relative">
                <button
                  onClick={() => setFontDropdownOpen((o) => !o)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition-colors ${isDark ? "bg-gray-800 hover:bg-gray-700 text-gray-200" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
                >
                  <span>A</span>
                  <span className="text-[10px]">{fontSize}</span>
                  <svg className={`w-3 h-3 transition-transform ${fontDropdownOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                </button>
                {fontDropdownOpen && (
                  <div className={`absolute top-full left-0 mt-1 w-24 rounded-xl shadow-2xl border z-50 overflow-hidden ${isDark ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"}`}>
                    {FONT_SIZES.map((s) => (
                      <button
                        key={s}
                        onClick={() => { setFontSize(s); setFontDropdownOpen(false); }}
                        className={`w-full px-3 py-1.5 text-xs text-left transition-colors ${
                          fontSize === s
                            ? "bg-gradient-to-r from-pink-500 to-blue-500 text-white"
                            : isDark ? "text-gray-300 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {s}px
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Reset */}
              <button
                onClick={handleResetCode}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${isDark ? "bg-gray-800 hover:bg-gray-700 text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-600"}`}
              >
                Reset
              </button>

              <div className="flex-1" />

              {/* Problem label */}
              <span className={`text-xs font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                {problems.findIndex((p) => p.id === currentProblem.id) + 1} / {problems.length}
              </span>
            </div>

            {/* Monaco editor */}
            <div className="flex-1 overflow-hidden">
              <Editor
                theme={isDark ? "vs-dark" : "vs"}
                language={toMonacoLanguage(language.toLowerCase())}
                value={code}
                onChange={(val) => setCode(val)}
                height="100%"
                options={{
                  fontSize,
                  fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  lineNumbers: "on",
                  renderLineHighlight: "line",
                  tabSize: 4,
                  wordWrap: "off",
                  padding: { top: 12, bottom: 12 },
                }}
              />
            </div>

            {/* ── BOTTOM RESIZE HANDLE ──────────────────────────────────────── */}
            {!isApexProblem && (
            <div
              ref={bottomResizeRef}
              onMouseDown={handleBottomMouseDown}
              className={`flex-none h-1.5 cursor-row-resize flex items-center justify-center group transition-colors ${isDark ? "bg-gray-800 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
            >
              <div className={`w-8 h-0.5 rounded-full transition-colors ${isDark ? "bg-gray-600 group-hover:bg-gray-400" : "bg-gray-400 group-hover:bg-gray-500"}`} />
            </div>
            )}

            {/* ── BOTTOM PANEL ─────────────────────────────────────────────── */}
            <div
              className={`flex-none flex flex-col border-t overflow-hidden ${isDark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-white"}`}
              style={isApexProblem ? undefined : { height: bottomPanelHeight }}
            >
              {isApexProblem && (
                <div className={`flex-none px-4 py-3 text-xs leading-relaxed border-b ${isDark ? "border-gray-700 bg-sky-900/20 text-sky-200" : "border-gray-100 bg-sky-50 text-sky-800"}`}>
                  <span className="font-semibold">AI-reviewed question.</span> Your Apex code is not executed. Write production-quality Apex:
                  bulk-safe, no SOQL or DML inside loops, handle null and empty input, and follow Salesforce best practices. You can edit and
                  re-submit until you end the test.
                </div>
              )}
              {!isApexProblem && (
              <>
              {/* Bottom tab bar */}
              <div className={`flex-none flex items-center justify-between border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                <div className="flex items-center">
                  {[
                    { key: "input",     label: "Custom Input" },
                    { key: "testcases", label: "Test Cases" },
                    { key: "output",    label: "Output" },
                  ].map(({ key, label }) => {
                    const active = bottomTab === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setBottomTab(key)}
                        className={`relative px-4 py-2 text-xs font-semibold transition-colors ${
                          active
                            ? isDark ? "text-white" : "text-gray-900"
                            : isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
                        }`}
                      >
                        {label}
                        {active && (
                          <span
                            className="absolute bottom-0 left-0 right-0 h-0.5"
                            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Run actions — kept beside the tabs that show their results */}
                <div className="flex items-center gap-2 pr-3">
                  <button
                    onClick={handleRunTestCases}
                    disabled={isRunning}
                    className={`px-3 py-1 text-xs font-semibold rounded-full border-2 transition-all disabled:opacity-50 ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:border-pink-500 hover:text-pink-400"
                        : "border-gray-300 text-gray-600 hover:border-pink-400 hover:text-pink-500"
                    }`}
                  >
                    {isRunning ? <span className="flex items-center gap-1"><Spinner size={11} /> Running…</span> : "Run Tests"}
                  </button>

                  <button
                    onClick={handleRun}
                    disabled={isRunning}
                    className="px-3 py-1 text-xs font-semibold rounded-full border-2 border-transparent transition-all disabled:opacity-50"
                    style={{
                      background: "transparent",
                      boxShadow: "0 0 0 2px #ec4899",
                      color: "#ec4899",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 0 2px #3b82f6"; e.currentTarget.style.color = "#3b82f6"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 0 0 2px #ec4899"; e.currentTarget.style.color = "#ec4899"; }}
                  >
                    {isRunning ? <span className="flex items-center gap-1"><Spinner size={11} /> Running…</span> : "▶ Run Code"}
                  </button>
                </div>
              </div>

              {/* Bottom tab content */}
              <div className="flex-1 overflow-y-auto p-3">
                {/* Custom Input */}
                {bottomTab === "input" && (
                  <div>
                    <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}>stdin</p>
                    <textarea
                      value={customInput}
                      onChange={(e) => setCustomInput(e.target.value)}
                      placeholder="Enter custom input..."
                      rows={4}
                      className={`w-full text-xs font-mono rounded-lg p-2.5 resize-none outline-none border transition-colors ${
                        isDark
                          ? "bg-gray-800 text-gray-200 border-gray-700 focus:border-pink-500 placeholder-gray-600"
                          : "bg-gray-50 text-gray-800 border-gray-200 focus:border-pink-400 placeholder-gray-400"
                      }`}
                    />
                  </div>
                )}

                {/* Test Cases */}
                {bottomTab === "testcases" && (
                  <div>
                    {testResults.length === 0 ? (
                      <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                        Click "Run Code" to execute against test cases.
                      </p>
                    ) : (
                      <div>
                        {/* Hidden test case summary */}
                        {hiddenTestCounts && (
                          <div className={`flex items-center gap-3 mb-3 px-3 py-2 rounded-lg text-xs font-medium border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
                            <span className={isDark ? "text-gray-400" : "text-gray-500"}>Hidden Tests:</span>
                            <span className="flex items-center gap-1 text-green-500 font-semibold">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              {hiddenTestCounts.passed} passed
                            </span>
                            {hiddenTestCounts.failed > 0 && (
                              <span className="flex items-center gap-1 text-red-500 font-semibold">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                                {hiddenTestCounts.failed} failed
                              </span>
                            )}
                            <span className={`ml-auto ${isDark ? "text-gray-500" : "text-gray-400"}`}>{hiddenTestCounts.passed}/{hiddenTestCounts.total}</span>
                          </div>
                        )}

                        {/* Case tabs */}
                        <div className="flex gap-2 mb-3 flex-wrap">
                          {testResults.map((r, i) => {
                            const statusLower = r.status?.toLowerCase() || "";
                            const passed = ["accepted", "correct", "pass"].includes(statusLower) ||
                              (r.yourOutput != null && r.desiredOutput != null &&
                                r.yourOutput.trim().replace(/\r\n/g, "\n") === r.desiredOutput.trim().replace(/\r\n/g, "\n"));
                            return (
                              <button
                                key={i}
                                onClick={() => setActiveTestCase(i)}
                                className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                                  activeTestCase === i
                                    ? passed
                                      ? "bg-green-500 text-white"
                                      : "bg-red-500 text-white"
                                    : passed
                                      ? isDark ? "bg-green-900/40 text-green-400" : "bg-green-50 text-green-600"
                                      : isDark ? "bg-red-900/40 text-red-400" : "bg-red-50 text-red-600"
                                }`}
                              >
                                {passed ? "✓" : "✗"} Case {i + 1}
                              </button>
                            );
                          })}
                        </div>
                        {/* Active case detail */}
                        {testResults[activeTestCase] && (() => {
                          const r = testResults[activeTestCase];
                          const statusLower = r.status?.toLowerCase() || "";
                          const passed = ["accepted", "correct", "pass"].includes(statusLower) ||
                            (r.yourOutput != null && r.desiredOutput != null &&
                              r.yourOutput.trim().replace(/\r\n/g, "\n") === r.desiredOutput.trim().replace(/\r\n/g, "\n"));
                          return (
                            <div className="space-y-2 text-xs">
                              <div className={`rounded-lg p-2 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                                <span className={`text-[10px] font-bold uppercase ${isDark ? "text-gray-500" : "text-gray-400"}`}>Input</span>
                                <pre className={`mt-1 font-mono ${isDark ? "text-gray-300" : "text-gray-700"}`}>{r.input}</pre>
                              </div>
                              <div className={`rounded-lg p-2 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                                <span className={`text-[10px] font-bold uppercase ${isDark ? "text-gray-500" : "text-gray-400"}`}>Expected</span>
                                <pre className={`mt-1 font-mono text-blue-400`}>{r.desiredOutput}</pre>
                              </div>
                              <div className={`rounded-lg p-2 ${passed ? isDark ? "bg-green-900/20" : "bg-green-50" : isDark ? "bg-red-900/20" : "bg-red-50"}`}>
                                <span className={`text-[10px] font-bold uppercase ${passed ? "text-green-500" : "text-red-500"}`}>Your Output</span>
                                <pre className={`mt-1 font-mono ${passed ? "text-green-400" : "text-red-400"}`}>{r.yourOutput || "(no output)"}</pre>
                              </div>
                              <div className={`flex items-center gap-1.5 text-xs font-semibold ${passed ? "text-green-500" : "text-red-500"}`}>
                                {passed ? "✓ Passed" : "✗ Wrong Answer"}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Output */}
                {bottomTab === "output" && (
                  <div>
                    {isRunning ? (
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <Spinner size={14} /> Running...
                      </div>
                    ) : error ? (
                      <div className={`rounded-lg p-3 border ${isDark ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"}`}>
                        <p className="text-xs font-bold text-red-500 mb-1">Error</p>
                        <pre className="text-xs font-mono text-red-400 whitespace-pre-wrap">{error}</pre>
                      </div>
                    ) : output ? (
                      <div>
                        <pre className={`text-xs font-mono whitespace-pre-wrap ${isDark ? "text-gray-300" : "text-gray-700"}`}>{output}</pre>
                      </div>
                    ) : (
                      <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>Run your code to see output here.</p>
                    )}
                  </div>
                )}
              </div>

              </>
              )}

              {/* ── ACTION FOOTER ──────────────────────────────────────────── */}
              <div className={`flex-none flex items-center justify-end gap-3 px-4 py-2.5 border-t ${isDark ? "border-gray-700" : "border-gray-100"}`}>
                <button
                  onClick={handleFinalSubmit}
                  disabled={isSubmitting || !code}
                  className="px-5 py-1.5 text-xs font-semibold text-white rounded-full transition-all disabled:opacity-50 hover:opacity-90 active:scale-95"
                  style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                >
                  {isSubmitting ? <span className="flex items-center gap-1"><Spinner size={12} /> Submitting…</span> : "Submit ✓"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Loading state */
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-transparent border-t-pink-500 border-r-blue-500 animate-spin" />
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>Loading problems…</p>
          </div>
        </div>
      )}

      {/* ── SUBMITTING OVERLAY ─────────────────────────────────────────────────── */}
      {isEndingTest && (
        <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-black/80 backdrop-blur-md">
          <div className="flex flex-col items-center gap-6 text-center px-6">
            {/* Animated rings */}
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 rounded-full border-4 border-white/10" />
              <div className="absolute inset-0 rounded-full border-4 border-t-pink-500 border-r-blue-500 border-b-transparent border-l-transparent animate-spin" />
              <div className="absolute inset-2 rounded-full border-4 border-t-transparent border-r-transparent border-b-pink-400 border-l-blue-400 animate-spin" style={{ animationDirection: "reverse", animationDuration: "0.8s" }} />
            </div>
            <div>
              <p className="text-white text-xl font-semibold mb-1">Submitting your test…</p>
              <p className="text-white/50 text-sm">Please do not close or refresh this page</p>
            </div>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full bg-white/60 animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── CONFIRMATION MODAL ─────────────────────────────────────────────────── */}
      {showDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <div className={`rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
            {/* Warning icon */}
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "linear-gradient(135deg, #fce7f3, #dbeafe)" }}>
              <IoWarningOutline className="text-pink-500 text-2xl" />
            </div>
            <h2 className={`text-lg font-bold text-center mb-1 ${isDark ? "text-white" : "text-gray-800"}`}>You have unsubmitted answers</h2>
            <p className={`text-xs text-center mb-5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Please submit your code for the problems below before ending — or end anyway. This action cannot be undone.
            </p>

            {/* Problem status grid */}
            <div className="grid grid-cols-2 gap-2 mb-6">
              {problems.map((p, i) => {
                const submitted = submittedProblems.has(p.id);
                return (
                  <div
                    key={p.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium ${
                      submitted
                        ? isDark ? "bg-green-900/30 text-green-400" : "bg-green-50 text-green-700"
                        : isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"
                    }`}
                  >
                    {submitted
                      ? <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      : <div className="w-3 h-3 rounded-full border border-current opacity-50" />
                    }
                    Problem {i + 1}
                    {submitted && <span className="ml-auto opacity-60">Saved</span>}
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDialog(false)}
                className={`flex-1 py-2.5 rounded-full text-sm font-semibold border transition-colors ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-800" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Go Back &amp; Submit
              </button>
              <button
                onClick={EndTest}
                className="flex-1 py-2.5 rounded-full text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
              >
                End Anyway ▸
              </button>
            </div>
          </div>
        </div>
      )}

      {isVideoProctoringEnabled && (
        <CodingProctoringSystem
          isEnabled={isVideoProctoringEnabled}
          interviewId={interview_id}
          isTestActive={isCodingTestActive}
          onMultipleFaceViolation={() => {}}
          onProhibitedObjectViolation={() => {}}
          onStickyAlert={handleStickyAlert}
          onFullscreenExitViolation={handleFullscreenExitViolation}
          onTabSwitchViolation={handleTabSwitchViolation}
          onFullscreenChange={() => {}}
        />
      )}

      {showProctoringAlert && (
        <ProctoringAlertModal
          isOpen={showProctoringAlert}
          message={proctoringAlertMessage}
          onAcknowledge={handleProctoringAcknowledge}
        />
      )}

      <AlertPanel
        getActiveAlerts={getActiveAlerts}
        alertLog={alertLog}
        stickyAlertCount={stickyAlertCount}
        isAlertPanelOpen={isAlertPanelOpen}
        handleAlertIconClick={handleAlertIconClick}
        currentAlertIndex={currentAlertIndex}
        navigateToPrevAlert={navigateToPrevAlert}
        navigateToNextAlert={navigateToNextAlert}
        handleAlertClick={handleAlertClick}
      />
    </div>
  );
}
